import moment from 'moment';

import { ContentData } from '@/contents/domains';

export const contents: ContentData[] = [
  {
    id: 'content00-0',
    latest: true,
    version: 1,
    type: 'text',
    name: '1) Javaとは',
    requiredTime: 10,
    body: {
      body: '# Javaとは\n\nルビテスト\nルビ{本文|ほんぶん}\n\n\n\n[リンクテスト -> Javaプログラミング例](#java%E3%83%97%E3%83%AD%E3%82%B0%E3%83%A9%E3%83%9F%E3%83%B3%E3%82%B0%E4%BE%8B)\n[外部リンクテスト -> Google](https://www.google.com/)\n\nJava（ジャヴァ）は、汎用プログラミング言語とソフトウェアプラットフォームの双方を指している総称ブランドである[6]。オラクル社およびその関連会社の登録商標である。1996年にサン・マイクロシステムズ社によって市場リリースされ、2010年に同社がオラクル社に吸収合併された事により、Javaの版権もそちらに移行した。\n\nプログラミング言語Javaは、C++に類似の構文、クラスベースのオブジェクト指向、マルチスレッド、ガーベジコレクション、コンポーネント指向、分散コンピューティングといった特徴を持ち、平易性重視のプログラム書式による堅牢性と、仮想マシン上での実行によるセキュリティ性およびプラットフォーム非依存性が理念とされている。Javaプラットフォームは、Javaプログラムの実行環境または、実行環境と開発環境の双方を統合したソフトウェアであり、ビジネスサーバー、モバイル機器、組み込みシステム、スマートカードといった様々なハードウェア環境に対応したソフトウェア形態で提供されている。その中枢技術であるJava仮想マシンは各プラットフォーム環境間の違いを吸収しながら、Javaプログラムの適切な共通動作を実現する機能を備えている[7]。このテクノロジは「write once, run anywhere」と標榜されていた[8]。\n\n2019年の時点でGitHubによると[9]、Javaは特にクライアント/サーバーモデルのWebアプリケーションで使用されている最も人気の高いプログラミング言語の1つであり[10]、全世界でおよそ900万人の開発者がいるとレポートされている[11]。最新バージョンは、2020年9月にリリースされたJava 15と、2018年9月にリリースされた長期サポート（LTS）版のJava 11である。オラクル社は未解決のセキュリティ問題によるリスクを回避するために、旧バージョンのアンインストールと新バージョンへの移行を強く推奨している[12]。\n> [6] “What is Java and why do I need it?” (英語). 2019年1月閲覧。\n> [7] “1.2 Design Goals of the Java™ Programming Language”. Oracle (1999年1月1日). 2013年1月23日時点のオリジナルよりアーカイブ。2013年1月14日閲覧。\n> [8] “Write once, run anywhere?”. Computer Weekly (2002年5月2日). 2009年7月27日閲覧。\n> [9] Chan (2019年1月22日). “The 10 most popular programming languages, according to the \'Facebook for programmers\'”. Business Insider. 2019年6月29日時点のオリジナルよりアーカイブ。2019年6月29日閲覧。\n> [10] Chan (2019年1月22日). “The 10 most popular programming languages, according to the \'Facebook for programmers\'”. Business Insider. 2019年6月29日時点のオリジナルよりアーカイブ。2019年6月29日閲覧。\n\n\n## Javaプログラミング例\n### Hello World\n\n```java\n//ハローワールド.java\npublic class Hello {\n    public static void main(String[] args) {\n        System.out.println("Hello, world!");\n    }\n}\n```\n\n- Javaプログラムでは全てのフィールドとメソッドがclass内に記述される。\n- Javaプログラムは静的メソッドpublic void static main (String[] args)から開始される。argsはプログラム開始時のコマンドライン・パラメータである。\n- System.outはSystemクラスの静的フィールドoutを意味する。outはPrintStream型のインスタンスである。out.println("文字列")はoutがprintlnメソッドを呼び出す。\n\n### キーワード一覧\nJavaの基本言語仕様は54種類程度に抑えたキーワードによって比較的コンパクトにまとめられている。Javaの構文はC++言語によく似たものであり、それよりも比較的平易化されている。従って以下のキーワードを眺めるだけでもJavaプログラミングの大まかなスタイルを掴むことができる。\n\n#### キーワード1\n\n| キーワード | 説明                       |\n| ---------- | ---------------------------|\n|import      | パッケージ名前空間の解決用 |\n|private     | 同クラス内でアクセス可     |\n|package     | 同クラス内と同パッケージ内でアクセス可、パッケージスコープの宣言 |\n|protected   | 同クラス内と同パッケージ内と派生クラス内でアクセス可 |\n|public      | 全範囲でアクセス可         |\n\n\n#### キーワード2\n\n| キーワード | 説明                       |\n| ---------- | ---------------------------|\n|import      | パッケージ名前空間の解決用 |\n|private     | 同クラス内でアクセス可     |\n|package     | 同クラス内と同パッケージ内でアクセス可、パッケージスコープの宣言 |\n|protected   | 同クラス内と同パッケージ内と派生クラス内でアクセス可 |\n|public      | 全範囲でアクセス可         |\n\n\n## Java認定資格\n\nオラクル[注釈 1]は複数のJava認定資格を主催している。Javaのバージョンアップに伴って資格も変更されることがある。ただし、変更前に取得した資格は変更後も有効である。認定試験に不合格だった場合、その試験日を含めて14日以内は同一試験を受験することができない。\n\n',
    },
    workbook: {
      problems: [
        {
          index: 0,
          type: 'choice',
          options: [
            {
              index: 0,
              text: 'option0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\n',
            },
            {
              index: 1,
              text: 'option0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\n',
            },
            {
              index: 2,
              text: 'option0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\n',
            },
            {
              index: 3,
              text: 'option0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\n',
            },
            {
              index: 4,
              text: 'option0-4\noption0-4\noption0-4\noption0-4\noption0-4\noption0-4\noption0-4\noption0-4\noption0-4\noption0-4\noption0-4\n',
            },
            {
              index: 5,
              text: 'option0-5\noption0-5\noption0-5\noption0-5\noption0-5\noption0-5\noption0-5\noption0-5\noption0-5\noption0-5\noption0-5\n',
            },
          ],
          multiple: true,
          answer: [0],
          body: 'this is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\n',
          commentary:
            'this is commentary.\nthis is commentary.\nthis is commentary.\nthis is commentary.\nthis is commentary.\n',
        },
        {
          index: 1,
          type: 'choice',
          options: [
            {
              index: 0,
              text: 'option0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\noption0-0\n',
            },
            {
              index: 1,
              text: 'option0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\noption0-1\n',
            },
            {
              index: 2,
              text: 'option0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\noption0-2\n',
            },
            {
              index: 3,
              text: 'option0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\noption0-3\n',
            },
          ],
          multiple: true,
          answer: [0],
          body: 'this is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\nthis is body\n',
          commentary: 'this is commentary',
        },
        {
          index: 2,
          type: 'choice',
          options: [
            { index: 0, text: 'option2-0' },
            { index: 1, text: 'option2-1' },
            { index: 2, text: 'option2-2' },
            { index: 3, text: 'option3-4' },
          ],
          multiple: false,
          answer: [0],
          body: '',
          headerId: '1',
          commentary: 'this is commentary',
        },
        {
          index: 3,
          type: 'choice',
          options: [
            { index: 0, text: 'option3-0' },
            { index: 1, text: 'option3-1' },
            { index: 2, text: 'option3-2' },
            { index: 3, text: 'option3-3' },
          ],
          multiple: false,
          answer: [0],
          body: '',
          headerId: '1',
          commentary: 'this is commentary',
        },
        {
          index: 4,
          type: 'choice',
          options: [
            { index: 0, text: 'option4-0' },
            { index: 1, text: 'option4-1' },
            { index: 2, text: 'option4-2' },
            { index: 3, text: 'option4-3' },
          ],
          multiple: false,
          answer: [0],
          body: '',
          headerId: '1',
          commentary: 'this is commentary',
        },
      ],
      problemHeaders: [
        {
          id: '0',
          body: '_____のことばの読み方として最もよいものを、1。 2・ 3・ 4か ら一つ えらびなさい。',
          createdAt: moment(),
        },
        {
          id: '1',
          body: '_____のことばを漢字で書 くとき、最もよいものを、1・ 2・ 3,4か ら、一つ えらびなさい。',
          createdAt: moment(),
        },
      ],
    },
    courseId: 'course-0',
    courseVersion: 1,
  },
  {
    id: 'content01-0',
    version: 1,
    latest: true,
    type: 'text',
    name: '2) メソッドとフィールド',
    requiredTime: 10,
    body: {
      body: '# 1. 仕事の基本\n今回は仕事をする上で意識しておきたい考え、取り組む姿勢を学びます。\n# 2. 仕事をするとは\n一般的に社会人と学生の違いは以下の通りです。\n|  社会人  |  学生  |\n| :---- | :---- |\n|  労働による成果を提供し、それに見合う給料を貰う。<br>給料に対する責任がある。<br>社員は給料に見合う労働力を提供する必要がある。  |  学費を払い、教育を受ける。<br>勉強するかどうかは自己責任。|\n<br>  \n学生は学費や教材費を払って学ぶ（知識や価値観を教えてもらう）、いわば「お客様」の立場です。  \n一方で社会人は、**貢献する立場**になります。報酬を受け取って所属している組織や社会に自分のスキルや商品、サービスを提供する側に変わります。  \nそのため、学生は本業（勉強）をするかどうかは本人の自由でしたが、**社会人になると本業（労働）は会社にとっても社会にとっても責任が伴う**ものになります。\n# 3. 何のために仕事をするのか？\n一番大きな理由として**自身が生活していくため**ということが考えられます。  \n給料を貰うことで電気代や家賃などの生活費や食費をまかない、日常生活を送ることができます。また、貯金をすることで老後や退職後も生きていくことができます。他にも自己実現のためや社会貢献のためといった理由がありますが、生活ができなければ元も子もないでしょう。\nでは、給料というものはどうやって発生するのでしょうか？  \n一番意識しておきたいのは、給料とは**自分が提供した労働（市場価値）に対する対価**だということです。  \n<br>\n⇒しかし、**自分の給料と同額の売上を上げていれば良いというわけではありません。**  \n<br>\n下図のように「会社に来て仕事をする」という行為自体にコストがかかっており、月あたり自分の給料の２～３倍の売上を会社にもたらさなければ、一般的に採算が取れないとされています。  \nまた、能力の高い人は会社に多くの利益をもたらすので給料が高く、逆に能力の低い人は会社に不利益をもたらすので給料が低いという関係が成り立ちます。  \nしたがって、**自分の能力を高める努力をする**必要があります。\n![sigoto_1.png](https://images.pexels.com/photos/416160/pexels-photo-416160.jpeg)\n# 4. 自分の能力を高めるために必要な努力とは？\n会社に利益をもたらした上で自分自身の能力を高めるためには以下のような意識が必要です。\n* 仕事の質の向上\n* 仕事スタイルの確立\n* 将来的な目標を立てる\n* 周りに興味を持つ\n## 4.1. 仕事の質の向上\n自身の能力（生産性）を高めるには**日々の仕事の質を高める**ことが重要です。  \n質の高い仕事とは以下のようなものを指します。  \n* 顧客満足度が高い  \n* 会社の利益が大きい  \n* 周りの人々の負担が減る  \n一度成功すれば良いのではなく、**常に質が高いこと**が大切です。 \n<br>  \n![sigoto_2.png](https://images.pexels.com/photos/416160/pexels-photo-416160.jpeg)\n<br>   \n## 4.2. 仕事スタイルの確立  \n仕事の質を上げることにも関連しますが、自分の仕事のスタイルを確立させることで結果的に能力の向上につながります。  \n仕事のスタイルを持つということは**仕事の質を高める為の自分なりの考えと意見を持つこと**です。  \n自分なりの考え・意見を持つためには「**PLAN・DO・SEE**」の意識を持つことが大切です。  \nこの意識がないと主体的に働くことができない、つまり他の人から指示された通りのことしかできない人になってしまい、作業スピードは向上するかもしれませんが、本質的には仕事の質の向上にはつながりません。\n* **PLAN（計画）**  \n    仕事の目的を理解したり、仕事の目標・やり方を考えたりすることです。  \n    計画を立てることで今後の展開と準備すべき事が明確になります。  \n  <br>\n* **DO（実行）**  \n    上司や先輩に相談をすることで計画を修正するべきか検討したり、実行途中に問題が発生した場合、修正したりすることです。  \n  <br>\n* **SEE（評価）**  \n    計画どおりに実行されたか確認したり、もっと良い方法はなかったのか反省し、つぎの計画に役立つような知識や技術の向上に努目たりすることです。   \n    見直しをする事で、成功・失敗の原因と同様の事態が起こった時の対処法を身に付けることができます。 \nしかし、**自分の仕事のスタイルを確立することに注力しすぎるとついつい独りよがりな仕事の進め方をしてしまう**恐れがあります。  \n独りよがりな仕事の進め方とは、「自分の考えが一番いい」「私が絶対正しい」という意識のもと、人の意見に耳を貸すことなく、自己中心的に働くことを指します。結果的にこの働き方は人の信用を失ったり、マネジメントを任せてもらえなかったりとデメリットが大きいです。  \n独りよがりな働き方を防ぐためにも評価や能力について意識を持つようにしましょう。  \n**<評価>**\n* 評価とは自分ではなく、他人がするものです。  \n* 努力しても求められる結果を出せなければ、評価はゼロに等しいです。  \n* 評価を上げるためには、まず評価をする人が何を望んでいるのかを考え、相手の満足感を満たすために何をすべきかを把握することが大切です。  \n  \n**<能力（価値）>**  \n* 仕事ができることだけが価値ではないです。  \n* メンバーとして、リーダーとして、マネージャーとして能力が発揮できる場合もあります。    \n  \n## 4.3. 将来的な目標を立てる  \n将来の目標（やりたい業務、就きたいポジションなど）を決めることで、目標に近づく努力をし、結果的に自分の能力向上につながります。  \n例えば「将来はチームリーダーのポジションに就きたい」と目標を立てた場合、それを実現するためにはマネジメントスキルやメンバーの教育スキル、仕事の生産性・正確性を向上させたり、習得する必要があります。それらのスキルや能力を得るためにはどのようにして働けばいいのかを「PLAN・DO・SEE」の意識を持って主体的に働くことで自身の能力向上につながるでしょう。\n目標を持つことのメリットは以下の通りです。\n* 目標に近づくために**何をすればよいかが明確になる**。  \n    ※逆に目標がなければ物事を進める時の過程が定まりません。  \n  <br>\n* 目標に近づく事でやりがいを感じ、やりがいを感じることで**仕事が楽しくなる**。  \n## 4.4. 周りに興味を持つ\n  仕事は一人でしているのではないので、**会社のことや人に興味を持つこと**も大事です。\n  興味を持つことで人間関係が円滑になるため、仕事を効率的に進めることができ、結果的に会社の利益にもつながります。\n  ＜例＞  \n* 会社の事や人に興味を持って色々なイベントや集まりに参加する。\n* 自分が関わっていないプロジェクトでも、興味を持って話を聞く。\n* 同期や同じプロジェクトのメンバーとのつながりを大切にする。\n  \n  そこで大事なのが「積極性」です。  \n  自分から知ろうとすればいくらでも知ることができるので、積極的に周りの人と関わりましょう。\n# 5. 仕事に取り組む姿勢\n会社の利益に貢献するため、自分の能力をさせるには何をすればいいのかを学びましたが、どのような姿勢で仕事に取り組めばいいのでしょうか。\n## 5.1. 会社・リーダーに求められる人材とは\nそもそもどのような人物が周りから求められるのでしょうか。\n* **積極的に仕事に取り組む人間**\n言われたことしかしない、消極的な態度で仕事に取り組む人間は周りにも迷惑をかけやすいです。\n<br>\n* **仕事を任せられる人間**\n安心して仕事を任せられる（逐一進捗状況を確認しなくていい）人はとても重宝されます。\n  * **積極性がある。** ※失敗の有無には関わらないです。\n  * **仕事を選り好みしない。** ※好き嫌いで仕事を選んでいると、仕事を任せてもらえなくなり、自分を伸ばす事が出来ません。  \n  \n   また、仕事を任せた後、**リーダーがその仕事について忘れてしまっていていい人間と、逐一気にしておかなければいけない人間とがいる**という意識も持ちましょう。  \n      ※前者：自分で考えて行動し、必要なときに報連相が出来る人間。  \n      ※後者：与えられた仕事について指示を待ち、報連相の出来ない人間。\n## 5.2. どのような姿勢で仕事に取り組むべきか\n### 5.2.1. 仕事を盗む\n  指示された仕事をこなすだけではなく、**周りの人の働きぶりからそれ以上の仕事を考え、こなす**ようにしましょう。  \n  そうすることで、上司・リーダーの負担が軽くなり、仕事が円滑に進みます。\n### 5.2.2. 仕事への責任を持つ\n* **どんな仕事でも自分の仕事だという認識を持つ。**  \n  プロジェクトリーダーではなく、メンバーやヘルプ・サポートとしてプロジェクトに入った場合でも、人の仕事ではなく自分の仕事だという認識で責任を持って仕事に臨むことが大事です。  \n  <br>\n* **自分の仕事に自信と誇りを持つ。**  \n  自分にとって嫌いな仕事・不得意な仕事でも、任された以上は自分の仕事であるので、手を抜いたり、人任せにしたりしてはいけません。  \n  ※最初は単純で簡単な仕事しか任されないかもしれませんが、どんな仕事でも大切にすることで徐々に大きな仕事を任されるようになります。\n### 5.2.3. 仕事での疑問と上手に向き合う\n* **分からないことを一人で抱え込まない。**  \n  分からないことを解決する方法を持ちましょう。  \n  ※人に聞く・何かを調べる等どうしたら疑問を解決出来るか、何がどのように分からないのかを整理することも重要です。   \n  <br>\n* **簡単に「出来ない」と言わない。**  \n  何もしないうちから「出来ない」と決めてしまわず、ひとまず挑戦してみる姿勢が大切です。  \n  ※やってみて出来なければ、「こういう理由で出来ませんでした。」と相談できた方が、相談された人間も力を貸しやすいです。\n### 5.2.4. 仕事における心掛けを大切にする\n* **向上心を持つ。**  \n  日々新しくなる情報を常に自分から取りに行く努力をしましょう。  \n  （休み時間に、専門雑誌をめくってみるなどして、最先端の動向をつかむ等。）  \n  <br>\n* **先輩の意見に耳を傾ける。**  \n  先輩が言うことは、経験や失敗に基づいての意見なので、ひとまず素直に聞き入れて、実行してみましょう。  \n  <br>\n* **常にコストを考える。**  \n  自分の仕事や、人の仕事に、常にコストがかかっていることを意識しましょう。  \n  作業や時間に常に損益が発生していることを意識することが大事です。  \n  ※例えば分からないことを誰かに教えてもらった時、メモを取らずに聞いていて、後日、同じことが分からなくなってもう一度聞いたとしたら、教えてくれた人の時間を倍使ってしまったことになります。  \n### 5.2.5. 仕事を楽しむ\n* **仕事をする中で自分なりに楽しみを見つけて取り組む。**  \n  自分だけでなく、周りの人間が気分良く仕事をすることにもつながります。  \n  <br>\n* **メリハリをつけて仕事をする。**  \n  繁忙期とそうでない時期があるので、力を入れる時と適度に力を抜く時のメリハリをつけて仕事に取り組みましょう。  \n* **自分の居場所を作る。**  \n  プロジェクトの中で、仕事的にも人間関係的にも、自分の居場所を作りましょう。  \n  （これによって、仕事がやりやすくなり、自分を向上させることもできます。）\n\n',
    },
    workbook: {
      problems: [
        {
          index: 0,
          type: 'choice',
          options: [
            { index: 0, text: 'option0' },
            { index: 1, text: 'option1' },
            { index: 2, text: 'option2' },
          ],
          multiple: false,
          answer: [0],
          body: 'this is body',
          commentary: 'this is commentary',
        },
        {
          index: 1,
          type: 'choice',
          options: [
            { index: 0, text: 'option0' },
            { index: 1, text: 'option1' },
            { index: 2, text: 'option2' },
          ],
          multiple: false,
          answer: [0],
          body: 'this is body',
          commentary: 'this is commentary',
        },
      ],
      problemHeaders: [],
    },
    courseId: 'course-0',
    courseVersion: 1,
  },
  {
    id: 'content02-0',
    version: 1,
    latest: true,
    type: 'text',
    name: '3) リテラル',
    requiredTime: 10,
    body: {
      body: '# リテラル\n\n',
    },
    courseId: 'course-0',
    courseVersion: 1,
  },
  {
    id: 'content03-0',
    version: 1,
    latest: true,
    type: 'exam',
    name: '4) 模擬試験A',
    requiredTime: 10,
    body: {
      problemHeaders: [
        {
          id: '0',
          body: '_____のことばの読み方として最もよいものを、1。 2・ 3・ 4か ら一つ えらびなさい。',
          createdAt: moment(),
        },
        {
          id: '1',
          body: '_____のことばを漢字で書 くとき、最もよいものを、1・ 2・ 3,4か ら、一つ えらびなさい。',
          createdAt: moment(),
        },
      ],
      problems: [
        {
          index: 0,
          type: 'choice',
          headerId: '0',
          body: '下記のコードを読み、`average()`メソッドの引数名と引数の型、戻り値の型の組み合わせとして正しいものを選択してください。\n\n```java\npublic class Average {\n  public static String average(int x, int y, int z) {\n    int sum = x + y + z;\n    int average = sum / 3;\n    return "3つの平均は" + average + "です";\n  }\n}\n```\n',
          options: [
            { index: 0, text: '引数はx(int型)、y(int型)、z(int型)、戻り値はString型' },
            { index: 1, text: '引数はx(int型)、y(int型)、z(int型)、戻り値はint型' },
            { index: 2, text: '引数はsum(int型)、average(int型)、戻り値はString型' },
            { index: 3, text: '引数はsum(int型)、average(int型)、戻り値はint型' },
          ],
          multiple: false,
          answer: [0],
          commentary: '解説',
        },
        {
          index: 1,
          type: 'choice',
          headerId: '1',
          body: '下記のコードの 空欄A に当てはめるものとして正しいコードを選択してください。\n\n```java\npublic class LargerNumber {\n  /**\n   * 大きいほうの値を返却する.\n   * @param x 数1\n   * @param y 数2\n   * @return xとyのうち、大きいほうの数\n   */\n  public static int max(int x, int y) {\n    if ([ A ]) {\n      return x;\n    }\n    return y;\n  }\n}\n```\n',
          options: [
            { index: 0, text: '`x > y`' },
            { index: 1, text: '`x >= y`' },
            { index: 2, text: '`x < y`' },
            { index: 3, text: '`x <= y`' },
            { index: 4, text: '`x != y`' },
          ],
          multiple: false,
          answer: [0],
          commentary: '解説',
        },
        {
          index: 2,
          type: 'choice',
          body: '以下のコードとオーバーロードの関係にあるものを選択してください。\n\n```java\npublic static void foo(int a) {\n}\n```\n',
          options: [
            { index: 0, text: '```java\npublic static void foo(String a) {\n}\n```\n' },
            { index: 1, text: '```java\npublic static void foo(int b) {\n}\n```\n' },
            { index: 2, text: '```java\npublic static void foo(int a, String b) {\n}\n```\n' },
            { index: 3, text: '```java\npublic static void bar(int a) {\n}\n```\n' },
            { index: 4, text: '```java\npublic static void bar(String a) {\n}\n```\n' },
          ],
          multiple: true,
          answer: [0, 2],
          commentary: '解説',
        },
        {
          index: 3,
          type: 'choice',
          body: '処理をする順番として正しいものを選んでください。\n\n```java\nint sum = 0;\nint[] scores = [2, 4];\nfor (int i = 0;(A) i < scores.length;(B) i++(C)) {\n  sum = sum + scores[i];(D)\n}\nreturn sum;(E)\n```\n',
          options: [
            { index: 0, text: 'A > B > D > C > B > D > C > B > E' },
            { index: 1, text: 'A > B > D > C > A > B > D > C > E' },
            { index: 2, text: 'A > B > C > D > A > B > C > D > E' },
            { index: 3, text: 'A > B > C > D > B > C > D > B > E' },
            { index: 4, text: 'A > D > C > B > D > C > B > D > E' },
          ],
          multiple: false,
          answer: [0],
          commentary: '解説',
        },
        {
          index: 4,
          type: 'choice',
          body: '正しい説明を選択してください。\n',
          options: [
            { index: 0, text: 'staticなメソッドからは、staticなフィールドにアクセスできる。' },
            { index: 1, text: 'staticでないメソッドからは、staticなフィールドにアクセスできる。' },
            { index: 2, text: 'staticなメソッドからは、staticなメソッドを呼び出せない。' },
            { index: 3, text: 'staticなメソッドからは、staticでないフィールドにアクセスできる。' },
            { index: 4, text: 'staticなメソッドからは、staticでないメソッドを呼び出せる。' },
            { index: 5, text: 'staticでないメソッドからは、staticなメソッドを呼び出せない。' },
          ],
          multiple: true,
          answer: [0, 1],
          commentary: '解説',
        },
        {
          index: 5,
          type: 'choice',
          body: 'body5',
          commentary: 'commentary5',
          options: [
            { index: 0, text: 'index5-0' },
            { index: 1, text: 'index5-1' },
            { index: 2, text: 'index5-2' },
            { index: 3, text: 'index5-3' },
            { index: 4, text: 'index5-4' },
          ],
          multiple: true,
          answer: [0],
        },
        {
          index: 6,
          type: 'choice',
          body: 'body6',
          commentary: 'commentary6',
          options: [
            { index: 0, text: 'index6-0' },
            { index: 1, text: 'index6-1' },
            { index: 2, text: 'index6-2' },
            { index: 3, text: 'index6-3' },
            { index: 4, text: 'index6-4' },
          ],
          multiple: true,
          answer: [0],
        },
        {
          index: 7,
          type: 'choice',
          body: 'body7',
          commentary: 'commentary7',
          options: [
            { index: 0, text: 'index7-0' },
            { index: 1, text: 'index7-1' },
            { index: 2, text: 'index7-2' },
            { index: 3, text: 'index7-3' },
            { index: 4, text: 'index7-4' },
          ],
          multiple: true,
          answer: [0],
        },
        {
          index: 8,
          type: 'choice',
          body: 'body8',
          commentary: 'commentary8',
          options: [
            { index: 0, text: 'index8-0' },
            { index: 1, text: 'index8-1' },
            { index: 2, text: 'index8-2' },
            { index: 3, text: 'index8-3' },
            { index: 4, text: 'index8-4' },
          ],
          multiple: false,
          answer: [0],
        },
        {
          index: 9,
          type: 'choice',
          body: 'body9',
          commentary: 'commentary9',
          options: [
            { index: 0, text: 'index9-0' },
            { index: 1, text: 'index9-1' },
            { index: 2, text: 'index9-2' },
            { index: 3, text: 'index9-3' },
            { index: 4, text: 'index9-4' },
          ],
          multiple: false,
          answer: [0],
        },
      ],
    },
    courseId: 'course-0',
    courseVersion: 1,
  },
  {
    id: 'content04-0',
    version: 1,
    latest: true,
    type: 'text',
    name: '5) コンテンツ',
    requiredTime: 10,
    body: {
      body: '本文',
    },
    courseId: 'course-0',
    courseVersion: 1,
  },
  {
    id: 'content05-0',
    version: 1,
    latest: true,
    type: 'text',
    name: '6) コンテンツ',
    requiredTime: 10,
    body: {
      body: '本文',
    },
    courseId: 'course-0',
    courseVersion: 1,
  },
  {
    id: 'content05-0',
    latest: true,
    version: 1,
    type: 'exam',
    name: '7) 模擬試験B',
    requiredTime: 10,
    body: {
      problems: [
        {
          index: 1,
          type: 'choice',
          body: '問題文',
          commentary: '解説',
          multiple: false,
          options: [
            { index: 0, text: '選択肢1' },
            { index: 1, text: '選択肢2' },
          ],
          answer: [0],
        },
      ],
      problemHeaders: [],
    },
    courseId: 'course-0',
    courseVersion: 1,
  },
  {
    id: 'content110-0',
    latest: true,
    version: 1,
    type: 'text',
    name: 'コンテンツ1',
    requiredTime: 10,
    body: {
      body: '本文\n',
    },
    courseId: 'course-11',
    courseVersion: 1,
  },
  {
    id: 'content120-0',
    latest: true,
    version: 1,
    type: 'text',
    name: 'コンテンツ1',
    requiredTime: 10,
    body: {
      body: '本文\n',
    },
    courseId: 'course-12',
    courseVersion: 1,
  },
  {
    id: 'content130-0',
    latest: true,
    version: 1,
    type: 'text',
    name: 'コンテンツ1',
    requiredTime: 10,
    body: {
      body: '本文\n',
    },
    courseId: 'course-13',
    courseVersion: 1,
  },
];
